import React, {
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

import { setError } from "../../reducers/appSlice";
import api from "../../service/api";
import BuscadorOrganismo from "../../commons/BuscadorOrganismo";

const Movimiento = forwardRef((props, ref) => {
  const { soporte, handleMovimiento } = props;
  const dispatch = useDispatch();
  const [buscandoOrganismo, setBuscandoOrganismo] = useState(false);

  var validationSchema = Yup.object().shape({
    folios: Yup.number().when(props.soporte, {
      is: (props) => soporte === "papel",
      then: Yup.number()
        .positive("Debe ser mayor que cero")
        .required("Requerido")
        .min(props.folios, "Debe ser mayor o igual a " + props.folios),
    }),
    cod_destino: Yup.number("Código numérico")
      .required("Requerido"),
    destino: Yup.string()
      .required("Requerido")
      .min(2, "Mínimo 2 caracteres"),
    observaciones: Yup.string().max(150, "Máximo 400 caracteres"),
  });

  const formik = useFormik({
    initialValues: {
      tipoInforme: 102,
      visibilidad: 1,
      fecha: null,
      folios: props.folios,
      cod_destino: "",
      destino: "",
      observaciones: "",
      id_tramite: props.id,
      mi_cod: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleMovimiento(values);
    },
    onReset: () => { },
  });

  const buscarOrganismoPorCodigo = (codigo) => {
    setBuscandoOrganismo(true);
    api
      .buscarOrganismo({
        id: codigo,
        descripcion: "",
        alta: "N",
        accion: "alta",
      })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          handleOrganismo(res.data[0]);
        } else {
          handleOrganismo();
          dispatch(setError("No se encontró el organismo"));
        }
      })
      .catch((e) => {
        handleOrganismo();
        dispatch(setError("Error al buscar el organismo"));
      })
      .finally(() => {
        setBuscandoOrganismo(false);
      });
  };

  const handleChangeTextField = (event) => {
    if (/^\d*$/.test(event.target.value)) {
      formik.setFieldValue(event.target.id, event.target.value);
    }
  };

  const handleChangeCodigoDestino = (event) => {
    if (/^\d*$/.test(event.target.value)) {
      formik.setFieldValue(event.target.id, event.target.value);
    }
    if (formik.values.destino !== "" && formik.values.destino !== null) {
      handleDestino();
      formik.setFieldValue("cod_destino", event.target.value);
    }
  };

  const handleOrganismo = (organismo) => {
    if (organismo != null) {
      formik.setFieldValue("cod_destino", organismo.id);
      formik.setFieldValue("destino", organismo.descOrgCompleta);
    } else {
      formik.setFieldValue("destino", "");
    }
  };

  const handleDestino = (destino = null) => {
    if (destino !== null) {
      alert(destino);
    } else {
      formik.setFieldValue("destino", "");
    }
  };

  const handleCodigoOrganismo = (event) => {
    if (event.keyCode === 13 || event.keyCode === 9) {
      if (event.target.value !== "" && event.target.value !== null) {
        buscarOrganismoPorCodigo(event.target.value);
      }
    } else if (
      event.type === "blur" &&
      event.target.value !== "" &&
      event.target.value !== null
    ) {
      buscarOrganismoPorCodigo(event.target.value);
    }
  };

  useImperativeHandle(ref, () => ({
    validarDatos() {
      formik.submitForm();
    },
  }));

  return (
    <>
      <form
        id="formAltaMovimiento"
        name="formAltaMovimiento"
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        noValidate
        autoComplete="off"
      >
        <Grid container spacing={3}>
          <Grid item lg={2} xs={12}>
            <TextField
              id="cod_destino"
              name="cod_destino"
              label="Código de organismo"
              variant="outlined"
              size="small"
              fullWidth
              value={formik.values.cod_destino}
              onChange={handleChangeCodigoDestino}
              onBlur={handleCodigoOrganismo}
              onKeyDown={handleCodigoOrganismo}
              error={
                formik.touched.cod_destino && Boolean(formik.errors.cod_destino)
              }
              helperText={
                formik.touched.cod_destino && formik.errors.cod_destino
              }
            />
          </Grid>
          <Grid item lg={9} xs={12}>
            <TextField
              id="destino"
              name="destino"
              label="Organismo de destino"
              variant="outlined"
              size="small"
              fullWidth
              value={formik.values.destino}
              onChange={formik.handleChange}
              error={formik.touched.destino && Boolean(formik.errors.destino)}
              helperText={formik.touched.destino && formik.errors.destino}
              disabled
            />
          </Grid>
          <Grid item lg={1} xs={12}>
            {buscandoOrganismo ? (
              <CircularProgress color="primary" size={20} sx={{ margin: 1 }} />
            ) : (
                <BuscadorOrganismo
                  resultado={handleOrganismo}
                  activo={"T"}
                  alta={"N"}
                  accion="alta"
                />
              )}
          </Grid>
        </Grid>
        {soporte === "papel" ? (
          <Grid container spacing={3} sx={{ marginTop: 0 }}>
            <Grid item lg={2} xs={12}>
              <TextField
                id="folios"
                name="folios"
                label="Folios" 
                variant="outlined"
                size="small"
                fullWidth
                inputProps={{ maxLength: 5 }}
                value={formik.values.folios}
                onChange={handleChangeTextField}
                error={formik.touched.folios && Boolean(formik.errors.folios)}
                helperText={formik.touched.folios && formik.errors.folios}
              />
            </Grid>
          </Grid>
        ) : (
            <Grid container spacing={3} sx={{ marginTop: 0 }}>
              <Grid item lg={2} xs={12} sx={{ display: "none" }}>
                <TextField
                  id="folios"
                  name="folios"
                  label="Folios"
                  type="number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={formik.values.folios}
                  onChange={formik.handleChange}
                  error={formik.touched.folios && Boolean(formik.errors.folios)}
                  helperText={formik.touched.folios && formik.errors.folios}
                />
              </Grid>
            </Grid>
          )}
        <br></br>
        <Grid container spacing={3}>
          <Grid item lg={12} xs={12}>
            <TextField
              id="observaciones"
              name="observaciones"
              label="Observaciones"
              variant="outlined"
              size="small"
              multiline
              rows={4}
              fullWidth
              value={formik.values.observaciones}
              onChange={formik.handleChange}
              error={
                formik.touched.observaciones &&
                Boolean(formik.errors.observaciones)
              }
              helperText={
                formik.touched.observaciones && formik.errors.observaciones
              }
            />
          </Grid>
        </Grid>
      </form>
      <br></br>
      <br></br>
    </>
  );
});

Movimiento.propTypes = {
  handleMovimiento: PropTypes.func.isRequired,
};

export default Movimiento;
